import type { AdminClient, AuditAnchor, AuditChainTable } from '@apx/admin-sdk';
import { wireDatePickers } from '../date-picker.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';

/**
 * Daily Merkle anchor browser.
 *
 * Lists every persisted AuditAnchor row (day × integrator × table → root /
 * rowCount / signature) and exposes a "Run now" button that calls
 * POST /v1/audit/anchors/run for yesterday's day (default) or a chosen
 * day. Idempotent — re-running for the same day overwrites the prior
 * row with the same root (rows are immutable past the 5-min window so
 * the input is frozen).
 *
 * The Merkle root + signature are the artifact you publish externally, so each
 * is rendered as a copyable chip (full value to clipboard, not the truncation).
 */

export interface AuditAnchorsPageDeps {
  client: AdminClient;
}

const TABLE_FILTERS: ReadonlyArray<{ value: AuditChainTable | ''; label: string }> = [
  { value: '', label: 'All tables' },
  { value: 'round_audit', label: 'Rounds' },
  { value: 'wallet_transaction', label: 'Wallet' },
  { value: 'admin_audit', label: 'Admin' },
];

const TABLE_LABEL: Record<string, string> = {
  round_audit: 'Rounds',
  wallet_transaction: 'Wallet',
  admin_audit: 'Admin',
};

export async function renderAuditAnchorsPage(
  root: HTMLElement,
  deps: AuditAnchorsPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Audit anchors</h1>
    <p class="page-sub">Daily tamper-evidence seal for the audit log. Publish the root externally and anyone can later prove a historic audit row hasn't been edited.</p>

    <details class="card" style="margin-bottom:16px">
      <summary style="cursor:pointer;list-style:none;font-weight:600;font-size:13px;display:flex;justify-content:space-between;align-items:center">
        <span>What is this and when do I use it?</span>
        <span class="muted" style="font-size:11px">click to expand</span>
      </summary>
      <div style="margin-top:12px;font-size:13px;line-height:1.6;color:var(--text-dim)">
        <p><strong style="color:var(--text)">What:</strong> every audit row is hash-chained to the previous one (git-style). Once a day, per (integrator × audit table), apx builds a Merkle tree over the day's row hashes → a single <strong>root hash</strong> + signature. That root is the anchor.</p>
        <p><strong style="color:var(--text)">Why:</strong> publish the daily root externally (timestamp service, customer-shared URL, regulator filing, public blockchain). Later, anyone can verify any historic audit row in <em>O(log N)</em> by recomputing only the Merkle proof path against the published root. If a row was backdated or edited, the proof fails — cryptographic, not "trust us".</p>
        <p><strong style="color:var(--text)">When to run:</strong> the nightly job handles this automatically once it's wired. Use <strong>Run anchor now</strong> manually for catch-up if the job missed a day, or after backfilling historic data. Re-running for the same day is idempotent — the input is frozen past the row's 5-minute mutability window.</p>
      </div>
    </details>

    <div id="aa-stats" class="players-stats"></div>

    <div class="card" style="margin-bottom:16px">
      <h2 style="margin-top:0">Run a batch</h2>
      <div class="row" style="align-items:flex-end;flex-wrap:wrap;margin-top:0">
        <span class="filter-field">
          <label for="aa-day">Day (UTC, defaults to yesterday)</label>
          <input type="date" id="aa-day" />
        </span>
        <button id="aa-run" class="primary">Run anchor now</button>
        <button id="aa-refresh" class="ghost">Refresh list</button>
        <span id="aa-run-result" class="muted" style="font-size:12px"></span>
      </div>
    </div>

    <div class="card">
      <div class="detail-card-head" style="display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap">
        <h2 style="margin:0">Anchored batches</h2>
        <div class="rtp-subtabs jp-seg" id="aa-tabs">
          ${TABLE_FILTERS.map(
            (t, i) =>
              `<button class="rtp-subtab${i === 0 ? ' active' : ''}" data-table="${t.value}">${t.label}</button>`,
          ).join('')}
        </div>
      </div>
      <div id="aa-content"><p class="muted">Loading anchors…</p></div>
    </div>
  `;

  wireDatePickers(root);

  const content = root.querySelector<HTMLDivElement>('#aa-content')!;
  const statsEl = root.querySelector<HTMLDivElement>('#aa-stats')!;
  const tabsEl = root.querySelector<HTMLDivElement>('#aa-tabs')!;
  const runBtn = root.querySelector<HTMLButtonElement>('#aa-run')!;
  const refreshBtn = root.querySelector<HTMLButtonElement>('#aa-refresh')!;
  const dayInput = root.querySelector<HTMLInputElement>('#aa-day')!;
  const runResult = root.querySelector<HTMLDivElement>('#aa-run-result')!;

  let tableFilter: AuditChainTable | '' = '';

  async function loadList(): Promise<void> {
    content.innerHTML = '<p class="muted">Loading anchors…</p>';
    try {
      const { anchors } = await deps.client.listAuditAnchors({
        limit: 200,
        ...(tableFilter ? { tableName: tableFilter } : {}),
      });
      renderStats(anchors);
      content.innerHTML = renderTable(anchors);
      wireCopy(content);
    } catch (err) {
      statsEl.innerHTML = '';
      content.innerHTML = `<p class="error">Failed to load anchors: ${escapeHtml(describeErr(err))}</p>`;
    }
  }

  function renderStats(anchors: AuditAnchor[]): void {
    if (anchors.length === 0) {
      statsEl.innerHTML = '';
      return;
    }
    const totalRows = anchors.reduce((acc, a) => acc + a.rowCount, 0);
    const days = new Set(anchors.map((a) => a.day)).size;
    const tables = new Set(anchors.map((a) => a.tableName)).size;
    const latest = anchors.reduce(
      (max, a) => (a.createdAt > max ? a.createdAt : max),
      anchors[0]!.createdAt,
    );
    const card = (label: string, value: string, sub?: string): string =>
      `<div class="stat-card"><div class="stat-card-label">${label}</div><div class="stat-card-value">${value}</div>${sub ? `<div class="stat-card-sub">${sub}</div>` : ''}</div>`;
    statsEl.innerHTML = [
      card('Batches', anchors.length.toLocaleString(), 'most recent 200'),
      card('Rows sealed', totalRows.toLocaleString()),
      card('Days covered', days.toLocaleString()),
      card('Audit tables', tables.toLocaleString()),
      card('Last anchored', fmtDateTime(latest)),
    ].join('');
  }

  tabsEl.querySelectorAll<HTMLButtonElement>('.rtp-subtab').forEach((btn) => {
    btn.addEventListener('click', () => {
      tableFilter = (btn.dataset.table ?? '') as AuditChainTable | '';
      tabsEl.querySelectorAll('.rtp-subtab').forEach((b) => b.classList.toggle('active', b === btn));
      void loadList();
    });
  });

  runBtn.addEventListener('click', async () => {
    runBtn.disabled = true;
    const day = dayInput.value || undefined;
    try {
      const res = await deps.client.runAuditAnchors(day);
      runResult.textContent = `Ran for ${res.day}: ${res.anchorCount} anchor(s) written.`;
      showToast(`Anchor job: ${res.anchorCount} row(s) written for ${res.day}`, 'success');
      await loadList();
    } catch (err) {
      showToast(`Anchor run failed: ${describeErr(err)}`, 'error');
    } finally {
      runBtn.disabled = false;
    }
  });

  refreshBtn.addEventListener('click', () => void loadList());

  await loadList();
}

function renderTable(anchors: AuditAnchor[]): string {
  if (anchors.length === 0) {
    return `<p class="muted">No anchors for this filter. Click <b>Run anchor now</b> to build a batch.</p>`;
  }
  return `
    <div style="overflow:auto"><table class="data" style="width:100%">
      <thead>
        <tr>
          <th>Day (UTC)</th>
          <th>Table</th>
          <th style="text-align:right">Rows</th>
          <th>Merkle root</th>
          <th>Signature</th>
          <th>Row range</th>
          <th>Anchored</th>
        </tr>
      </thead>
      <tbody>
        ${anchors
          .map(
            (a) => `
          <tr>
            <td><b>${escapeHtml(a.day)}</b></td>
            <td><span class="pill" title="${escapeHtml(a.tableName)}">${escapeHtml(TABLE_LABEL[a.tableName] ?? a.tableName)}</span></td>
            <td style="text-align:right">${a.rowCount.toLocaleString()}</td>
            <td>${hashChip(a.merkleRoot)}</td>
            <td>${hashChip(a.signature)}</td>
            <td class="mono" style="font-size:11px">${a.firstRowId ? shortId(a.firstRowId) : '—'} … ${a.lastRowId ? shortId(a.lastRowId) : '—'}</td>
            <td style="white-space:nowrap">${fmtDateTime(a.createdAt)}</td>
          </tr>
        `,
          )
          .join('')}
      </tbody>
    </table></div>
  `;
}

/** Truncated hash + copy button — the full value (the publish artifact) goes to
 *  the clipboard, not the on-screen truncation. */
function hashChip(value: string): string {
  return `<span class="hash-chip"><code title="${escapeHtml(value)}">${escapeHtml(shortHash(value))}</code><button type="button" class="hash-copy" data-copy="${escapeHtml(value)}" title="Copy full value">copy</button></span>`;
}

function wireCopy(scope: HTMLElement): void {
  scope.querySelectorAll<HTMLButtonElement>('[data-copy]').forEach((btn) => {
    btn.addEventListener('click', () => {
      void navigator.clipboard?.writeText(btn.dataset.copy ?? '').then(
        () => showToast('Copied to clipboard.', 'success'),
        () => showToast('Copy failed.', 'error'),
      );
    });
  });
}

function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}
function shortHash(h: string): string {
  return h.length > 16 ? `${h.slice(0, 12)}…` : h;
}
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function describeErr(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
